import {IconButton} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, ThemeContainer, ThemeProvider} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CloseLine} from '@dropbox/dig-icons/assets';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {isMobileAtom} from 'atoms/layout';
import cx from 'classnames';
import {Avatar} from 'components/DSYS/Avatar';
import {format} from 'date-fns';
import {useAtomValue} from 'jotai';
import {Fragment, ReactNode} from 'react';

import styles from './Banner.module.css';

export const BANNER_IMAGE_BOX = {width: 200, height: 113};

/**
 * The home banner's visual shell, shared by the calendar-driven banners and
 * managed ones.
 *
 * Calendar-driven banners always use dark text on a background that fades to
 * white. With `followsTheme`, the text and the faded-to color come from the
 * surrounding DIG theme instead, so a banner can have a dark-mode look.
 */
export const BannerView = ({
  title,
  subtitle,
  marquee,
  background,
  followsTheme,
  image,
  imageBackground,
  imageNode,
  coverImage,
  onDismiss,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Omit to hide the scrolling strip. */
  marquee?: {text: string; eventStart?: Date};
  /** A CSS background image, e.g. a gradient. */
  background: string;
  followsTheme?: boolean;
  image?: string;
  imageBackground?: string;
  imageNode?: ReactNode;
  /** Crop the image to fill its card instead of stretching it. */
  coverImage?: boolean;
  onDismiss?: () => void;
}) => {
  const isMobile = useAtomValue(isMobileAtom);
  const copy = (
    <>
      <Text className={styles.brandSharp}>{title}</Text>
      <Text>{subtitle}</Text>
    </>
  );

  return (
    <Box
      borderRadius="Medium"
      borderColor="Text Base"
      borderStyle="Solid"
      marginBottom="32"
      marginTop="16"
      position="relative"
      className={cx(followsTheme && styles.themedBackdrop)}
      style={
        followsTheme
          ? {backgroundImage: background, minHeight: 124}
          : // Banner text is always dark, so gradients that fade out must land on white in dark mode too.
            {background: `${background}, #FFFFFF`, minHeight: 124}
      }
    >
      {marquee && (
        <Box
          paddingY="6"
          backgroundColor="Background Base"
          borderColor="Text Base"
          borderBottom="Solid"
          style={{borderTopLeftRadius: 8, borderTopRightRadius: 8}}
        >
          <Marquee text={marquee.text} eventStart={marquee.eventStart} />
        </Box>
      )}

      <Box
        style={{
          marginLeft: isMobile ? 16 : BANNER_IMAGE_BOX.width + 56,
          marginTop: (isMobile ? BANNER_IMAGE_BOX.height / 2 + 24 : 0) + (marquee ? 0 : 12),
        }}
        paddingY="12"
        display="flex"
        flexDirection="column"
      >
        {followsTheme ? (
          copy
        ) : (
          <ThemeProvider mode="bright">
            <ThemeContainer>{copy}</ThemeContainer>
          </ThemeProvider>
        )}
      </Box>

      <Video
        background={imageBackground}
        image={image}
        imageNode={imageNode}
        coverImage={coverImage}
        // A themed banner's image card is solid even without an image, so the
        // banner doesn't show through it.
        isCardOpaque={followsTheme}
        {...BANNER_IMAGE_BOX}
      />

      {onDismiss && (
        <Box className={styles.closeButton}>
          <IconButton size="small" variant="filled" aria-label="Dismiss banner" onClick={onDismiss}>
            <UIIcon src={CloseLine} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

const Video = ({
  background,
  image,
  imageNode,
  floatingImage,
  circular,
  coverImage,
  isCardOpaque,
  ...boxSize
}: {
  image?: string;
  imageNode?: ReactNode;
  background?: string;
  floatingImage?: boolean;
  circular?: boolean;
  coverImage?: boolean;
  isCardOpaque?: boolean;
  width: number;
  height: number;
}) => {
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  return (
    <Box position="absolute" top="0" left="0">
      <Box position="relative" style={boxSize}>
        <Box
          position="absolute"
          backgroundColor="Background Base"
          style={{...boxSize, left: 36, bottom: 1}}
          borderRadius={circular ? 'Circular' : 'XLarge'}
          borderColor="Text Base"
          borderStyle="Solid"
        />
        <Box
          position="absolute"
          backgroundColor="Background Base"
          style={{...boxSize, left: 24, bottom: 9}}
          borderRadius={circular ? 'Circular' : 'XLarge'}
          borderColor="Text Base"
          borderStyle="Solid"
          bottom="0"
          right="0"
        />
        {circular ? (
          <>
            <Box
              position="absolute"
              style={{...boxSize, left: 12, bottom: 17, background}}
              borderRadius={circular ? 'Circular' : 'XLarge'}
              borderColor="Text Base"
              borderStyle="Solid"
              bottom="0"
              right="0"
            />
            <Box
              position="absolute"
              style={{left: 23, top: -6, transform: 'scale(1.16)', objectFit: 'cover'}}
              borderRadius="Circular"
              overflow="hidden"
            >
              <Avatar key="april-fool" size="profile" user={employee} />
            </Box>
          </>
        ) : image && !floatingImage ? (
          <img
            style={{
              ...boxSize,
              left: 12,
              bottom: 17,
              background,
              objectFit: coverImage ? 'cover' : undefined,
            }}
            src={image}
            alt=""
            className={atoms({
              position: 'absolute',
              backgroundColor: 'Background Base',
              borderRadius: circular ? 'Circular' : 'XLarge',
              borderColor: 'Text Base',
              borderStyle: 'Solid',
              bottom: '0',
              right: '0',
            })}
          />
        ) : (
          <>
            <Box
              position="absolute"
              backgroundColor={isCardOpaque && !background ? 'Background Base' : undefined}
              style={{...boxSize, left: 12, bottom: 17, background}}
              borderRadius={circular ? 'Circular' : 'XLarge'}
              borderColor="Text Base"
              borderStyle="Solid"
              bottom="0"
              right="0"
            />
            {imageNode && (
              <Box
                position="absolute"
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{...boxSize, left: 12, bottom: 17}}
              >
                {imageNode}
              </Box>
            )}
            {image && (
              <img
                style={{left: 24, bottom: 10, transform: 'scale(1.06)', objectFit: 'cover'}}
                src={image}
                alt=""
                className={atoms({
                  position: 'absolute',
                  borderRadius: circular ? 'Circular' : 'XLarge',
                })}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

const Marquee = ({text, eventStart}: {text: string; eventStart?: Date}) => {
  const items = <MarqueeItems text={text} eventStart={eventStart} />;

  return (
    <div className={styles.marquee}>
      <Text className={styles.marqueeSlider} isBold size="xsmall">
        <Box
          display="inline-flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          style={{gap: 12, textTransform: 'uppercase'}}
        >
          {items}
        </Box>
        <Box
          display="inline-flex"
          flexDirection="row"
          paddingX="12"
          alignItems="center"
          justifyContent="center"
          style={{gap: 12, textTransform: 'uppercase'}}
        >
          {items}
        </Box>
      </Text>
    </div>
  );
};

/** `$DATE` alternates the event's date and time; anything else repeats as written. */
const MarqueeItems = ({text, eventStart}: {text: string; eventStart?: Date}) => {
  const datePieces =
    text === '$DATE' && eventStart
      ? [
          format(eventStart, 'EEE, MMM d'),
          `${format(eventStart, 'h:mma').toLowerCase()} ${
            Intl.DateTimeFormat(undefined, {timeZoneName: 'short'})
              .formatToParts(eventStart)
              .find((part) => part.type === 'timeZoneName')?.value || ''
          }`,
        ]
      : undefined;

  return (
    <>
      {Array.from({length: 15}).map((_, i) => (
        <Fragment key={i}>
          <span>{datePieces ? datePieces[i % 2] : text}</span>
          <span>—</span>
        </Fragment>
      ))}
    </>
  );
};
